import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaHeart, FaComment, FaUser, FaShare, FaEnvelope, FaCheckDouble, FaAt, FaTimes, FaTrash } from 'react-icons/fa';
import { notificationsAPI } from '../utils/api';
import { getAvatarUrl } from '../utils/avatar';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const ICON_MAP = {
  like: <FaHeart className="text-[#FF0055]" size={14} />,
  comment: <FaComment className="text-[#4DD9E8]" size={14} />,
  follow: <FaUser className="text-[#4A90E2]" size={14} />,
  repost: <FaShare className="text-[#A855F7]" size={14} />,
  message: <FaEnvelope className="text-[#4A90E2]" size={14} />,
  mention: <FaAt className="text-[#4DD9E8]" size={14} />,
};

const NotificationsDropdown = ({ mobileMode = false }) => {
  const [isOpen, setIsOpen] = useState(mobileMode);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);
  const clearCooldownRef = useRef(0);
  const navigate = useNavigate();

  const loadUnreadCount = useCallback(async () => {
    try {
      // Skip polling during cooldown after clear (prevents "ghost" notifications)
      if (Date.now() - clearCooldownRef.current < 15000) return;
      const res = await notificationsAPI.getUnreadCount();
      const newCount = res.data.count;
      // Show toast if new notifications arrived
      if (newCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const diff = newCount - prevUnreadRef.current;
        if (diff > 0 && prevUnreadRef.current > 0) {
          toast(
            <div className="flex items-center space-x-2">
              <FaBell className="text-[#4A90E2]" />
              <span className="text-sm font-medium">
                {diff === 1 ? 'New notification' : `${diff} new notifications`}
              </span>
            </div>,
            { duration: 3000 }
          );
        }
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch (err) {
      // silent
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data);
    } catch (err) {
      // silent
    }
  }, []);

  // Poll unread count every 10 seconds
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  // Refresh notification list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [isOpen, loadNotifications, loadUnreadCount]);

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationsAPI.markRead(notification.id);
        setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      setIsOpen(false);

      switch (notification.type) {
        case 'follow':
          if (notification.related_user_id) {
            navigate(`/profile/${notification.related_user_id}`);
          }
          break;
        case 'message':
          navigate('/messages');
          break;
        case 'like':
        case 'comment':
        case 'mention':
          if (notification.related_post_id) {
            navigate(`/feed?post=${notification.related_post_id}`);
          } else {
            navigate('/feed');
          }
          break;
        default:
          navigate('/feed');
      }
    } catch (err) {
      console.error('Notification click error', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDismiss = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.dismiss(notificationId);
      const removed = notifications.find(n => n.id === notificationId);
      const remaining = notifications.filter(n => n.id !== notificationId);
      setNotifications(remaining);
      if (removed && !removed.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1);
      }
      if (remaining.length === 0) {
        clearCooldownRef.current = Date.now();
      }
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationsAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
      prevUnreadRef.current = 0;
      clearCooldownRef.current = Date.now();
      if (!mobileMode) setIsOpen(false);
      toast.success('Notifications cleared');
    } catch (err) {
      toast.error('Failed to clear notifications');
    }
  };

  // Shared notification list renderer
  const renderNotificationList = () => (
    <>
      {/* Header with mark all read & clear all */}
      {!mobileMode && (
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-gray-900 font-bold text-base">Notifications</h3>
          <div className="flex items-center space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center space-x-1 text-xs text-[#4A90E2] hover:text-[#3A80D2] font-medium transition-colors"
                data-testid="mark-all-read-btn"
              >
                <FaCheckDouble size={10} />
                <span>Mark read</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="flex items-center space-x-1 text-xs text-red-400 hover:text-red-500 font-medium transition-colors"
                data-testid="clear-all-btn"
              >
                <FaTrash size={10} />
                <span>Clear all</span>
              </button>
            )}
          </div>
        </div>
      )}
      {mobileMode && (notifications.length > 0 || unreadCount > 0) && (
        <div className="px-4 py-2 border-b border-gray-100 flex items-center space-x-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center space-x-1 text-xs text-[#4A90E2] hover:text-[#3A80D2] font-medium transition-colors"
              data-testid="mark-all-read-btn-mobile"
            >
              <FaCheckDouble size={10} />
              <span>Mark read</span>
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={handleClearAll}
              className="flex items-center space-x-1 text-xs text-red-400 hover:text-red-500 font-medium transition-colors"
              data-testid="clear-all-btn-mobile"
            >
              <FaTrash size={10} />
              <span>Clear all</span>
            </button>
          )}
        </div>
      )}

      {/* Notification items */}
      <div className={mobileMode ? "" : "overflow-y-auto max-h-[400px]"}>
        {notifications.length === 0 ? (
          <div className="py-12 px-6 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaBell className="text-xl text-gray-300" />
            </div>
            <p className="text-gray-800 font-semibold">No notifications yet</p>
            <p className="text-gray-400 text-xs mt-1">We&apos;ll notify you when something happens</p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => handleNotificationClick(n)}
              className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-50 group ${
                !n.is_read ? 'bg-[#4A90E2]/5' : ''
              }`}
              data-testid={`notification-${n.id}`}
            >
              <div className="flex items-start space-x-3">
                {n.related_user_avatar || n.related_user_username ? (
                  <img
                    src={getAvatarUrl(n.related_user_avatar, n.related_user_username || '?')}
                    alt=""
                    className="mt-0.5 w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="mt-0.5 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    {ICON_MAP[n.type] || <FaBell className="text-gray-400" size={14} />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-sm leading-snug">{n.content}</p>
                  <p className="text-gray-400 text-xs mt-1">{timeAgo(n.created_at)}</p>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {!n.is_read && (
                    <div className="w-2 h-2 bg-[#4A90E2] rounded-full" />
                  )}
                  <button
                    onClick={(e) => handleDismiss(e, n.id)}
                    className="p-1 text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`dismiss-${n.id}`}
                    title="Dismiss"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  // Mobile mode: render list inline without bell button
  if (mobileMode) {
    return renderNotificationList();
  }

  // Desktop mode: bell button + dropdown
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-gray-600 hover:text-gray-900 transition-colors"
        data-testid="notifications-button"
      >
        <FaBell className="text-2xl" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1.5 -right-1.5 bg-[#FF0055] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed right-4 top-16 md:right-auto md:left-[240px] md:top-16 w-80 sm:w-96 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden max-h-[80vh]"
              data-testid="notifications-dropdown"
            >
              {renderNotificationList()}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationsDropdown;
